import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import { formatDate } from '../utils/dateFormatter';
import useAnalytics from '../analytics/analytics';
import { DateRange } from "react-day-picker";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Bot, Code, Headset, Info, Globe, Mail, Monitor, MoreHorizontal, Sparkles, Trash2, User } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ExportSessionsButton from './ExportSessionsButton';
import { ModerationBadge, type SessionModerationData } from './SessionModeration';
import PageSizeSelect, { DEFAULT_PAGE_SIZE } from '@/components/ui/PageSizeSelect';
import { useTableFilters, DataTableToolbar, useTableSort, SortControl, SortHeader } from '@/components/shared/filters';
import { buildSessionsFilters, SESSIONS_SORTS } from './sessionsFilters';
import debounce from 'lodash/debounce';
import { AimdocComponentContext } from '@aimdoc/sdk-react';
import {
  getIdentityDisplayName,
  getIdentityVerificationTitle,
  getLeadDisplayLabel,
  resolveIdentityStatus,
  type VisitorProfilePayload,
} from '../../lib/visitorIdentity';
import { cn } from '@/lib/utils';
import VisitorIdentitySummary from '../identity/VisitorIdentitySummary';

interface SessionsProps {
  agentId?: string;
  leadId?: string;
  dashboard?: boolean;
  embedded?: boolean;
}

interface Session {
  id: string;
  type: string;
  created_at: string;
  last_activity: string;
  agent_id?: string;
  human_agent_id?: string;
  is_human_agent?: boolean;
  moderation?: SessionModerationData | null;
  human_agent?: {
    id: string;
    name?: string;
    email?: string;
    profile_photo_url?: string | null;
  };
  lead?: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    company?: string | null;
    job_title?: string | null;
    identity_verified_at?: string | null;
  };
  visitor_profile?: VisitorProfilePayload | null;
  status: string;
  initiated_by?: string;
}

interface Agent {
  id: string;
  parent_agent_id: string;
  name?: string;
  persona?: string;
  goal?: string;
  type?: string;
}

interface Lead {
  id: string;
  email: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
}

const getResponsiveColumnClass = (columnId: string) => {
  switch (columnId) {
    case 'last_activity':
      return 'hidden 2xl:table-cell';
    case 'duration':
    case 'source':
    case 'initiated_by':
    case 'rep':
      return 'hidden xl:table-cell';
    case 'owner':
      return 'hidden lg:table-cell';
    default:
      return '';
  }
};

const SessionActions: React.FC<{
  onDelete: () => void | Promise<void>;
}> = ({ onDelete }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  return (
    <div data-row-actions onClick={(event) => event.stopPropagation()}>
      <AimdocComponentContext
        componentDescription="This menu contains actions for this session, including delete."
        componentName="Session Actions Menu"
        path="/sessions"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            aria-label="Session actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete session
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete session?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this session? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteDialogOpen(false);
                void onDelete();
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const Sessions: React.FC<SessionsProps> = ({ agentId, leadId, dashboard, embedded }) => {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [selectedLead, setSelectedLead] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>();
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const [selectedReps, setSelectedReps] = useState<string[]>([]);
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const navigate = useNavigate();
  const location = useLocation();
  const { get, remove } = useApi();
  const { captureEvent } = useAnalytics();
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [allReps, setAllReps] = useState<{id: string; name?: string; email: string; profile_photo_url?: string | null}[]>([]);
  
  const getDateRange = (filter: string): DateRange | undefined => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (filter) {
      case 'today': {
        return { from: today, to: today };
      }
      case 'last3days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 2);
        return { from: start, to: today };
      }
      case 'lastWeek': {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return { from: start, to: today };
      }
      case 'lastMonth': {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        return { from: start, to: today };
      }
      case 'last90days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 89);
        return { from: start, to: today };
      }
      case 'lastYear': {
        const start = new Date(today);
        start.setDate(start.getDate() - 364);
        return { from: start, to: today };
      }
      default:
        return undefined;
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const agentsResponse = await get('/agents/');
      if (agentsResponse?.data && Array.isArray(agentsResponse.data)) {
        setAllAgents(agentsResponse.data.map(agent => ({
          id: agent.id,
          parent_agent_id: agent.parent_agent_id,
          name: agent.name || agent.id,
          type: agent.type 
        })));
      } else {
        console.error('Unexpected agents response format:', agentsResponse);
        setAllAgents([]);
      }

      const leadsResponse = await get('/leads/');
      if (leadsResponse?.data && Array.isArray(leadsResponse.data)) {
        setAllLeads(leadsResponse.data.map(lead => ({
          id: lead.id,
          email: lead.email,
          phone: lead.phone,
        })));
      } else {
        console.error('Unexpected leads response format:', leadsResponse);
        setAllLeads([]);
      }

      // Fetch reps (users in organization)
      const repsResponse = await get('/users/');
      if (repsResponse?.data && Array.isArray(repsResponse.data)) {
        setAllReps(repsResponse.data.map((user: any) => {
          // Construct full name from first_name and last_name
          const nameParts: string[] = [];
          if (user.first_name) nameParts.push(user.first_name);
          if (user.last_name) nameParts.push(user.last_name);
          const fullName: string | undefined = nameParts.length > 0 ? nameParts.join(' ') : undefined;
          
          return {
            id: user.id as string,
            name: fullName,
            email: user.email as string,
            profile_photo_url: user.profile_photo_url || null,
          };
        }));
      } else {
        console.error('Unexpected reps response format:', repsResponse);
        setAllReps([]);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      setAllAgents([]);
      setAllLeads([]);
      setAllReps([]);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentPage = parseInt(params.get('page') || '1');
    const perPageParam = parseInt(params.get('per_page') || String(DEFAULT_PAGE_SIZE), 10);
    const resolvedPerPage = Number.isFinite(perPageParam) && perPageParam > 0 ? perPageParam : DEFAULT_PAGE_SIZE;
    const agentParam = params.get('agent_id') || 'all';
    const visitorIdParam = params.get('visitor_id') || undefined;
    const leadParam = params.get('lead_id') || 'all';
    const isActiveParam = params.get('is_active');
    const statusParam = isActiveParam === 'true' ? 'active' :
      isActiveParam === 'false' ? 'inactive' : 'all';
    const dateFilterParam = params.get('date_filter') || 'all';
    const searchParam = params.get('search') || '';
    const repsParam = params.getAll('human_agent_ids');
    const sourceParam = params.get('conversation_type') || 'all';

    setPage(currentPage);
    setPerPage(resolvedPerPage);
    setSelectedAgent(agentParam);
    setSelectedLead(leadParam);
    setSelectedStatus(statusParam);
    setSelectedDateFilter(dateFilterParam);
    setSearchInput(searchParam);
    setSelectedReps(repsParam);
    setSelectedSource(sourceParam);

    if (dateFilterParam !== 'all') {
      const newRange = getDateRange(dateFilterParam);
      if (newRange) {
        setDateRange(newRange);
      }
    } else {
      setDateRange(undefined);
    }

    getSessions(currentPage, searchParam, statusParam, leadParam, dateFilterParam, agentParam, visitorIdParam, repsParam, resolvedPerPage);
  }, [location.search]);

  const getSessions = async (
    page: number = 1,
    search: string = '',
    status: string = 'all',
    leadFilter: string = selectedLead,
    dateFilter: string = selectedDateFilter,
    agentFilter: string = selectedAgent,
    visitorId?: string,
    repsFilter: string[] = selectedReps,
    perPageArg: number = perPage,
  ) => {
    try {
      setLoading(true);
      const baseParams = new URLSearchParams({
        page: page.toString(),
        per_page: perPageArg.toString(),
        search,
      });

      if (dateFilter !== 'all') {
        const dateFilterMap: { [key: string]: string } = {
          'today': 'today',
          'last3days': 'last_3_days',
          'lastWeek': 'last_week',
          'lastMonth': 'last_month',
          'last90days': 'last_90_days',
          'lastYear': 'last_year'
        };
        
        const backendDateFilter = dateFilterMap[dateFilter];
        if (backendDateFilter) {
          baseParams.set('date_range', backendDateFilter);
        }
      }

      if (visitorId) {
        baseParams.set('visitor_id', visitorId);
      }

      if (leadId) {
        baseParams.set('lead_id', leadId);
      } else if (leadFilter !== 'all') {
        baseParams.set('lead_id', leadFilter);
      }

      if (dashboard) {
        baseParams.set('is_active', 'true');
      } else if (status !== 'all') {
        baseParams.set('is_active', status === 'active' ? 'true' : 'false');
      }

      if (agentFilter !== 'all') {
        baseParams.set('agent_id', agentFilter);
      }

      // Add rep filtering (OR logic for multiple reps)
      if (repsFilter.length > 0) {
        repsFilter.forEach(repId => {
          baseParams.append('human_agent_ids', repId);
        });
      }

      const sourceParam = new URLSearchParams(location.search).get('conversation_type');
      if (sourceParam && sourceParam !== 'all') {
        baseParams.set('conversation_type', sourceParam);
      }

      const initiatedByParam = new URLSearchParams(location.search).get('initiated_by');
      if (initiatedByParam && initiatedByParam !== 'all') {
        baseParams.set('initiated_by', initiatedByParam);
      }

      if (new URLSearchParams(location.search).get('moderation') === 'flagged') {
        baseParams.set('moderation', 'flagged');
      }

      const sortParams = new URLSearchParams(location.search);
      const sortByVal = sortParams.get('sort_by');
      const sortOrderVal = sortParams.get('sort_order');
      if (sortByVal) baseParams.set('sort_by', sortByVal);
      if (sortOrderVal) baseParams.set('sort_order', sortOrderVal);

      const data = await get(`/sessions/?${baseParams.toString()}`);
      setSessions(data.data.items);
      setPage(data.data.page);
      setTotalPages(data.data.pages);

      captureEvent('viewed_sessions', {});
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSessions([]);
      setTotalPages(1);
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await remove(`/sessions/${id}`);
      setSessions(sessions.filter((agent: any) => agent.id !== id));
      toast.success('Session deleted successfully', toastConfig);
      captureEvent('delete_session', {
        session_id: id,
      });
    } catch (error) {
      console.error('Error deleting Session:', error);
    }
    setLoading(false);
  };

  const {
    activeField: sortField,
    activeOrder: sortOrder,
    isDefault: isDefaultSort,
    setSort,
    toggleField,
    clear: clearSort,
  } = useTableSort();

  const renderSortHeader = (label: string, field: string) => (
    <SortHeader
      label={label}
      field={field}
      activeField={sortField}
      activeOrder={sortOrder}
      onSort={(f) => toggleField(f, SESSIONS_SORTS.find((s) => s.field === f)?.defaultOrder)}
    />
  );

  const columns = React.useMemo<ColumnDef<Session, any>[]>(
    () => [
      {
        accessorKey: 'created_at',
        size: 160,
        header: () => renderSortHeader('Session Started', 'created_at'),
        cell: ({ row }) => (
          <div>
            {formatDate(row.getValue('created_at'), 'compact')}
          </div>
        ),
      },
      {
        accessorKey: 'last_activity',
        size: 150,
        header: () => renderSortHeader('Last Active', 'last_activity'),
        cell: ({ row }) => (
          <div>
            {formatDate(row.getValue('last_activity'), 'compact')}
          </div>
        ),
      },
      {
        id: 'duration',
        size: 100,
        accessorFn: (row) => {
          const start = new Date(row.created_at);
          const end = new Date(row.last_activity);
          return Math.floor((end.getTime() - start.getTime()) / 1000);
        },
        header: () => <span>Duration</span>,
        cell: ({ row }) => {
          const session = row.original;
          const start = new Date(session.created_at);
          const end = new Date(session.last_activity);
          const diffInSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);
          const minutes = Math.floor(diffInSeconds / 60);
          const seconds = diffInSeconds % 60;
          return (
            <div>
              {`${minutes}m ${seconds}s`}
            </div>
          );
        },
      },
      {
        id: 'source',
        accessorKey: 'type',
        size: 90,
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>Source</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Where the session originated</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
        cell: ({ row }) => {
          const type = row.original.type;
          const channel = (row.original as any).channel;
          if (channel === 'email') {
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-help">
                      <Mail className="h-4 w-4 text-amber-500" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent><p>Email</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          if (channel === 'gateway') {
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-help">
                      <Bot className="h-4 w-4 text-cyan-500" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent><p>AI Gateway</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          if (type === 'app') {
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-help">
                      <Monitor className="h-4 w-4 text-blue-500" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent><p>App</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          if (type === 'both') {
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-help">
                      <Globe className="h-4 w-4 text-green-500" />
                      <Monitor className="h-4 w-4 text-blue-500 -ml-1.5" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent><p>Website + App</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center cursor-help">
                    <Globe className="h-4 w-4 text-green-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent><p>Website</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        id: 'initiated_by',
        accessorFn: (row) => row.initiated_by || 'visitor',
        size: 90,
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>Started By</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Who started the conversation</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
        cell: ({ row }) => {
          const initiatedBy = row.original.initiated_by || 'visitor';
          const config: { [key: string]: { icon: React.ReactNode; label: string } } = {
            visitor: { icon: <User className="h-4 w-4 text-muted-foreground" />, label: 'Started by the visitor' },
            ai: { icon: <Sparkles className="h-4 w-4 text-purple-500" />, label: 'Started by the AI' },
            human: { icon: <Headset className="h-4 w-4 text-emerald-500" />, label: 'Started by a rep' },
            sdk: { icon: <Code className="h-4 w-4 text-orange-500" />, label: 'Seeded via the SDK' },
          };
          const { icon, label } = config[initiatedBy] || config.visitor;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center cursor-help">{icon}</div>
                </TooltipTrigger>
                <TooltipContent><p>{label}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        id: 'visitor',
        accessorFn: (row) => getIdentityDisplayName({
          lead: row.lead,
          person: row.visitor_profile?.person,
          company: row.visitor_profile?.company,
        }) || '',
        size: 220,
        header: () => <span>Visitor</span>,
        cell: ({ row }) => {
          const session = row.original;
          const identityStatus = resolveIdentityStatus({
            lead: session.lead,
            person: session.visitor_profile?.person,
            company: session.visitor_profile?.company,
          });

          return (
            <div className="flex min-w-0 items-center gap-2">
              <ModerationBadge moderation={session.moderation} />
              <VisitorIdentitySummary
                status={identityStatus}
                lead={session.lead}
                person={session.visitor_profile?.person}
                company={session.visitor_profile?.company}
                contactHref={session.lead ? `/contacts/${session.lead.id}` : undefined}
                verifiedTitle={getIdentityVerificationTitle({
                  leadIdentityVerifiedAt: session.lead?.identity_verified_at,
                  sessionIdentityVerifiedAt: session.visitor_profile?.identity_verified_at,
                })}
              />
            </div>
          );
        },
      },
      {
        id: 'owner',
        accessorFn: (row) => row.is_human_agent ? 'human' : 'ai',
        size: 100,
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>Owner</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Who is currently interacting with the visitor</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
        cell: ({ row }) => {
          const session = row.original;
          const isHumanOwned = session.is_human_agent && !!session.human_agent_id;
          
          // Generate a consistent color based on email
          const getColorFromEmail = (email: string) => {
            const colors = [
              'bg-red-500',
              'bg-blue-500',
              'bg-green-500',
              'bg-yellow-500',
              'bg-purple-500',
              'bg-pink-500',
              'bg-indigo-500',
              'bg-orange-500',
              'bg-teal-500',
              'bg-cyan-500',
            ];
            
            let hash = 0;
            for (let i = 0; i < email.length; i++) {
              hash = email.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
          };
          
          // Determine tooltip text and avatar details
          let tooltipText = 'AI Agent';
          let initial = 'H';
          let colorClass = 'bg-blue-500';
          
          if (isHumanOwned) {
            if (session.human_agent) {
              const email = session.human_agent.email || '';
              const name = session.human_agent.name || '';
              
              tooltipText = name || email || 'Human Agent';
              initial = (email.charAt(0) || name.charAt(0) || 'H').toUpperCase();
              colorClass = getColorFromEmail(email || session.human_agent_id || '');
            } else {
              // Fallback when human_agent data is not available
              tooltipText = 'Human Agent';
              initial = 'H';
              colorClass = getColorFromEmail(session.human_agent_id || '');
            }
          }
          
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center cursor-help">
                    {isHumanOwned ? (
                      <Avatar className="h-6 w-6 border">
                        <AvatarImage src={session.human_agent?.profile_photo_url || undefined} alt={tooltipText} />
                        <AvatarFallback className={`${colorClass} text-white font-semibold text-xs`}>
                          {initial}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <span
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full border bg-muted text-muted-foreground"
                        aria-label="AI agent"
                      >
                        <Bot className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        id: 'rep',
        accessorFn: (row) => row.human_agent?.email || row.human_agent?.name || '',
        size: 90,
        header: () => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">Rep</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>The last human rep to own this conversation</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
        cell: ({ row }) => {
          const session = row.original;
          const rep = session.human_agent;

          if (!rep) {
            return (
              <Badge variant="outline" className="text-muted-foreground">
                None
              </Badge>
            );
          }

          const colors = [
            'bg-red-500',
            'bg-blue-500',
            'bg-green-500',
            'bg-yellow-500',
            'bg-purple-500',
            'bg-pink-500',
            'bg-indigo-500',
            'bg-orange-500',
            'bg-teal-500',
            'bg-cyan-500',
          ];
          const identity = rep.email || rep.name || session.human_agent_id || '';
          let hash = 0;
          for (let i = 0; i < identity.length; i++) {
            hash = identity.charCodeAt(i) + ((hash << 5) - hash);
          }

          const tooltipText = rep.name || rep.email || 'Rep';
          const initial = (rep.email?.charAt(0) || rep.name?.charAt(0) || 'R').toUpperCase();
          const colorClass = colors[Math.abs(hash) % colors.length];

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Avatar className="h-6 w-6 border cursor-help">
                    <AvatarImage src={rep.profile_photo_url || undefined} alt={tooltipText} />
                    <AvatarFallback className={`${colorClass} text-white font-semibold text-xs`}>
                      {initial}
                    </AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        accessorKey: 'status',
        size: 110,
        header: () => renderSortHeader('Status', 'status'),
        cell: ({ row }) => {
          const status = row.getValue('status');
          return (
            <div className="flex justify-start">
              {status === 'active' ? (
                <Badge variant="secondary" className="">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <div className="absolute">
                    <div className="w-4 h-4 -ml-1 bg-green-500 rounded-full animate-ping opacity-75"></div>
                  </div>
                  Active
                </Badge>
              ) : (
                <Badge variant="outline" className="text-slate-500">
                  Inactive
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: 'actions',
        size: 56,
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <SessionActions onDelete={() => handleDelete(row.original.id)} />
        ),
      },
    ],
    [handleDelete, formatDate, sortField, sortOrder, toggleField]
  );

  // Updated useReactTable configuration with TanStack pagination
  const table = useReactTable<Session>({
    data: sessions,
    columns,
    manualPagination: true, // enable manual pagination
    pageCount: totalPages,  // use totalPages from API response
    state: {
      pagination: {
        pageIndex: page - 1,  // TanStack page indexing is 0-based
        pageSize: perPage,
      },
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === 'function'
          ? updater({ pageIndex: page - 1, pageSize: perPage })
          : updater;
      const newPage = newState.pageIndex + 1;
      const params = new URLSearchParams(location.search);
      params.set("page", newPage.toString());
      navigate(`?${params.toString()}`);
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePerPageChange = (value: number) => {
    const params = new URLSearchParams(location.search);
    params.set('per_page', String(value));
    params.set('page', '1');
    navigate(`?${params.toString()}`);
  };

  // Update the agentOptions memo
  const agentOptions = React.useMemo(() => {
    return [
      { value: 'all', label: 'All Agents' },
      ...allAgents.map(agent => ({
        value: agent.parent_agent_id,
        label: agent.name || agent.parent_agent_id
      }))
    ];
  }, [allAgents]);

  // Replace the uniqueLeadOptions memo with this
  const leadOptions = React.useMemo(() => {
    const options = [
      { value: 'all', label: 'All Contacts' },
      ...(Array.isArray(allLeads) ? allLeads.map(lead => ({
        value: lead.id,
        label: getLeadDisplayLabel(lead)
      })) : [])
    ];
    return options;
  }, [allLeads]);

  // Shared filter toolbar registry. URL params match the legacy inline filters
  // exactly, so the existing URL effect + getSessions + ExportSessionsButton are
  // unchanged; this only drives the toolbar UI.
  const sessionsFilters = React.useMemo(
    () => buildSessionsFilters({
      agentOptions,
      leadOptions,
      repOptions: (allReps || []).map((rep: any) => ({ value: rep.id, label: rep.name || rep.email })),
      includeContact: !leadId,
    }),
    [agentOptions, leadOptions, allReps, leadId],
  );
  const {
    values: filterValues,
    setFilter,
    clearFilter,
    clearAll,
    searchValue,
    setSearch,
  } = useTableFilters(sessionsFilters);

  return (
    <div className={dashboard || embedded ? "" : "p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow"}>
      {!dashboard && !embedded ?
        <>
          <div className="flex flex-row justify-between items-end">
            <div className="flex flex-col">
              <h1 className='text-4xl font-bold flex items-center gap-2'>Sessions</h1>
              <p className='mt-4 text-sm text-slate-500'>Explore every conversation had between your customers and agents. Click on a session to view more details.</p>
            </div>
            {/* <InteractiveHoverButton className="mr-12">
              Analyze Sessions with AI
            </InteractiveHoverButton> */}
          </div>
          <Separator className="my-4" />
        </>
        : (
          <></>
        )}
      <div className="mt-2 flex w-full flex-row bg-transparent">
        <div className="w-full grow">
          <DataTableToolbar
            className="mb-4"
            filters={sessionsFilters}
            values={filterValues}
            onChange={setFilter}
            onClear={clearFilter}
            onClearAll={clearAll}
            search={{
              value: searchValue,
              onChange: setSearch,
              placeholder: 'Search contacts and conversations...',
            }}
            rightSlot={
              <SortControl
                sorts={SESSIONS_SORTS}
                activeField={sortField}
                activeOrder={sortOrder}
                isDefault={isDefaultSort}
                onSelect={setSort}
                onClear={clearSort}
              />
            }
          />
          <div className="mt-4 flex flex-col">
            <div className="-m-1.5 overflow-x-auto scrollbar-hidden">
              <div className="p-1.5 w-full align-middle">
                <div className="border rounded-lg shadow overflow-hidden">
                  <Table className="table-fixed w-full">
                    <TableHeader>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <TableHead
                              key={header.id}
                              style={{ width: header.getSize() }}
                              className={cn('truncate', getResponsiveColumnClass(header.column.id))}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    {loading ? (
                      <TableBody>
                        {[1, 2].map((i) => (
                          <TableRow key={i} className="animate-pulse">
                            {columns.map((column: any, index) => (
                              <TableCell
                                key={column.id || column.accessorKey}
                                className={cn('px-6 py-4', getResponsiveColumnClass(column.id || column.accessorKey))}
                              >
                                <div className={cn('h-4 bg-muted rounded', index % 2 ? 'w-1/2' : 'w-3/4')} />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    ) : (
                      <TableBody>
                        {table.getRowModel().rows?.length ? (
                          table.getRowModel().rows.map((row) => (
                            <TableRow
                              key={row.id}
                              className="cursor-pointer"
                              onClick={(event) => {
                                if ((event.target as HTMLElement).closest('[data-row-actions]')) {
                                  return;
                                }
                                navigate(`/sessions/${row.original.id}`);
                              }}
                            >
                              {row.getVisibleCells().map((cell) => (
                                <TableCell
                                  key={cell.id}
                                  style={{ width: cell.column.getSize() }}
                                  className={cn('overflow-hidden', getResponsiveColumnClass(cell.column.id))}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={columns.length}
                              className="h-24 text-center"
                            >
                              No sessions.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )}
                  </Table>
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center mt-4">
              <PageSizeSelect value={perPage} onChange={handlePerPageChange} disabled={loading} />
              {totalPages > 1 && (
                <div className='flex flex-row items-center gap-4'>
                  <Button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    variant="outline"
                  >
                    Previous
                  </Button>
                  <span className="text-sm font-semibold">
                    {`Page ${page} of ${totalPages}`}
                  </span>
                  <Button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    variant="outline"
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
            <ExportSessionsButton
              selectedAgent={selectedAgent}
              selectedLead={selectedLead}
              selectedStatus={selectedStatus}
              selectedDateFilter={selectedDateFilter}
              selectedInitiatedBy={new URLSearchParams(location.search).get('initiated_by') || 'all'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sessions;
